import {Component, OnInit} from '@angular/core';
import {AbstractControl, AsyncValidatorFn, FormControl, FormGroup, ValidationErrors, ValidatorFn} from '@angular/forms';
import {SchemaService} from '../../services/schema.service';
import {ValidationService} from '../../services/validation.service';
import {ErrorNotificationService} from '../../services/error-notification.service';
import {AuthService} from '../../services/auth.service';
import {NewUserDto} from '../../common/dtos';
import {BBEnv} from '../../environments/bb-env';
import {AlertController, NavController, Platform} from '@ionic/angular';
import {TranslateService} from '@ngx-translate/core';
import {TranslationService} from '../../services/translation.service';
import {LoadingService} from '../../services/loading.service';
import {MatomoTracker} from 'ngx-matomo-client';
import {BBErrorType} from 'src/errors/bb-error';
import {delay, from, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {RestService} from '../../services/rest.service';

@Component({
  selector: 'app-signup',
  templateUrl: './signup.page.html',
  styleUrls: ['./signup.page.scss']
})
export class SignupPage implements OnInit {
  username = new FormControl('', this.validatorFnForProperty('username'), [this.usernameValidator()]);
  email = new FormControl('', this.validatorFnForProperty('email'), [this.emailIsUsedValidator()]);
  password = new FormControl('', this.validatorFnForProperty('password'));
  confirmPassword = new FormControl(
    '',
    this.validatorFnForProperty('password')
  );
  isIos = false;

  usernameIsFree = true;
  emailIsUsed = false;
  signupFormGroup = new FormGroup({
    username: this.username,
    email: this.email,
    password: this.password,
    confirmPassword: this.password
  });

  constructor(
    private restService: RestService,
    private matomoTracker: MatomoTracker,
    private loadingService: LoadingService,
    public platform: Platform,
    private alertCtrl: AlertController,
    private router: NavController,
    private schemaService: SchemaService,
    private validationService: ValidationService,
    private errorNotificationService: ErrorNotificationService,
    private authService: AuthService,
    private translate: TranslateService,
    private translationService: TranslationService
  ) {

  }

  ionViewWillEnter() {
    this.username.setValue('');
    this.email.setValue('');
    this.password.setValue('');
    this.confirmPassword.setValue('');
    this.isIos = this.platform.is('ios');
    this.emailIsUsed = false;
    this.usernameIsFree = true;
  }

  isValid(): boolean {
    return (
      this.signupFormGroup.valid &&
      this.password.value == this.confirmPassword.value
    );
  }

  private validatorFnForProperty(property: string): ValidatorFn[] {
    return this.validationService.getFormValidator(
      this.schemaService.signupCredentialsSchema,
      property
    );
  }

  public usernameValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return from(this.restService.usernameIsFree(control.value)).pipe(delay(500)).pipe(
        map(usernameIsFree => {
          this.usernameIsFree = usernameIsFree;
          return !usernameIsFree ? {usernameIsFree: false} : null;
        })
      );
    };
  }

  public emailIsUsedValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return from(this.restService.emailIsAlreadyUsed(control.value)).pipe(delay(500)).pipe(
        map(emailIsUsed => {
          this.emailIsUsed = emailIsUsed;
          return emailIsUsed ? {emailIsUsed: true} : null;
        })
      );
    };
  }

  async onSignup() {
    const loader = await this.loadingService.createAndShowDefaultLoadingMessage();
    try {
      await this.authService.signup({
        username: this.username.value,
        email: this.email.value,
        password: this.password.value,
        language: this.translationService.getCurrentLanguage()
      } as NewUserDto);
      const alert = await this.alertCtrl.create({
        header: this.translate.instant('sign-up-page.wait-for-confirmation'),
        message: this.translate.instant('sign-up-page.confirmation-email'),
        cssClass: 'bb-alert',
        buttons: [
          {
            text: 'OK',
            id: 'signnup-ok'
          }
        ]
      });
      await alert.present();
      this.matomoTracker.trackEvent('auth', 'sign-up');
      await this.router.navigateRoot('/login/' + this.username.value + '/' + this.password.value);
    } catch (error) {
      if (error?.bbErrorType === BBErrorType.USER_ALREADY_EXISTS) {
        this.errorNotificationService.showBBError(error);
      } else {
        throw error;
      }
    } finally {
      await loader.dismiss();
    }
  }

  isProd() {
    return BBEnv.isProduction();
  }

  onTestUser() {
    // convenience method for development
    this.username.setValue('testuser');
    this.email.setValue('test@user.de');
    this.password.setValue('testuser');
    this.confirmPassword.setValue('testuser');
  }

  ngOnInit() {
  }

  emailInvalid() {
    return this.email.value != '' && !this.email.valid;
  }

  emailValid() {
    return this.email.value != '' && this.email.valid;
  }

  usernameValid() {
    return this.username.value != '' && this.username.valid;
  }

  usernameInvalid() {
    return this.username.value != '' && !this.username.valid;
  }

  passwordValid() {
    return this.password.value != '' && this.password.valid;
  }

  passwordInvalid() {
    return this.password.value != '' && !this.password.valid;
  }

  confirmPasswordInvalid() {
    return this.confirmPassword.value != '' && this.passwordValid() && this.password.value != this.confirmPassword.value;
  }

  confirmPasswordValid() {
    return this.passwordValid() && this.password.value == this.confirmPassword.value;
  }

}
